import type { ReactNode } from "react";
import { FeatureFlagConfig } from "@/app/config/FeatureFlagConfig";
import type {
	AbstractLeadFormProps,
	AbstractLeadFormState,
} from "@/component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@/component/assets/AbstractLeadForm/AbstractLeadForm";
import type { LeadRequest, ProductType } from "@/component/assets/AbstractLeadForm/types";
import type { EventComponentType } from "@/shared/lib/google-analytics-4.type";
import { logError } from "@/shared/lib/sentry-error";
import { formatText } from "@/shared/ui/format";
import { calculate, calculateOld, getCheapestOffer } from "./domain";
import * as Step from "./Steps";
import type {
	FormFields,
	LifeInsuranceLeadCalcRequest,
	LifeInsuranceLeadCalcResponse,
	RiskInsuranceResponse,
	RiskInsuranceResponseMap,
} from "./types";

const FEATURE_FLAG_NAME = "bankmonitor-hu-webapp.enable-risk-insurance-calculation-v2";

interface State<TFormFields extends FormFields = FormFields> extends AbstractLeadFormState<TFormFields> {
	calculatorResponse: RiskInsuranceResponseMap;
	showDetectiveAlert: boolean;
}

/**
 * @Shortcode
 *
 * @title "Kockázati kalkulátor"
 * @shortcode "risk_insurance_small"
 * @slug "kockazati-biztositas"
 * @type "calculator"
 */
export class RiskInsuranceSmallCalculator extends AbstractLeadForm<
	LifeInsuranceLeadCalcRequest,
	LifeInsuranceLeadCalcResponse,
	FormFields,
	AbstractLeadFormProps<LifeInsuranceLeadCalcRequest, LifeInsuranceLeadCalcResponse>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "LIFE_INSURANCE";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		outsideTitle: "Kockázati életbiztosítás",
		outsideTitleLink: "/biztositas/eletbiztositas/",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			calculatorResponse: {},
			showDetectiveAlert: false,
		};
	}

	public componentDidMount(): void {
		super.componentDidMount();

		// NOTE: This is a workaround to work well with prerender
		this.setState({ showDetectiveAlert: !window.location.search.includes("disableDetective") });
	}

	protected renderSteps(): ReactNode {
		const {
			values: { email },
			submitState,
			showDetectiveAlert: enabledDetective,
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" showDetectiveAlert={enabledDetective} />
				<Step.Step2 stepName="step-2" defaultTarget="result" submitState={submitState} canStepBack />
				<Step.Result stepName="result" defaultTarget="lead-form" canStepBack />
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					button="Eredmény küldése"
					submitState={submitState}
				/>
				<Step.SuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.onResult(target);
				break;
			case "success-page":
				this.onSuccessPage();
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	private onResult(target: string): void {
		const { values } = this.state;

		this.setState({ submitState: "loading" });

		this.promisePool
			.add(FeatureFlagConfig.isEnabled(FEATURE_FLAG_NAME) ? calculate(values) : calculateOld(values))
			.then((calculatorResponse: RiskInsuranceResponseMap) => {
				if (typeof calculatorResponse !== "object" || !Object.keys(calculatorResponse)?.length) {
					throw new Error("Invalid calculator response");
				}

				this.setState(
					{
						calculatorResponse,
						submitState: undefined,
					},
					() => {
						super.onStep(target, true);
						this.sendViewItemListEvent();
					},
				);

				this.sendCalculationEvent();
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				logError(error);
			});
	}

	private onSuccessPage(): void {
		this.setState({
			submitState: "loading",
		});
		this.sendLead()
			.then((_response) => {
				super.onStep("success-page", false);
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	protected getLeadData(): LeadRequest<LifeInsuranceLeadCalcRequest, LifeInsuranceLeadCalcResponse> {
		const {
			calculatorResponse = {},
			values: { riskInsuranceAmount, mainInsuranceGoal, question, birthYear, durationYears },
		} = this.state;
		const {
			anyDisabilityInsuranceAmount,
			fatalAccidentInsuranceAmount,
			dreadDiseaseInsuranceAmount,
			accidentDisabilityInsuranceAmount,
			anyDisabilityInsurancePrice,
			fatalAccidentInsurancePrice,
			accidentDisabilityInsurancePrice,
			dreadDiseaseInsurancePrice,
			riskPrice,
		} = getCheapestOffer(calculatorResponse);
		const data = super.getLeadData();

		data.request.question = question;
		data.request.birthYear = birthYear;
		data.request.preferredProduct =
			mainInsuranceGoal === "RISK_INSURANCE" ? "Kockázati életbiztosítás" : "Hitelfedezeti védelem";

		data.request.isCreditInsurance = mainInsuranceGoal === "CREDIT_INSURANCE";
		data.request.isRiskInsurance = mainInsuranceGoal === "RISK_INSURANCE";
		data.request.isDisabilityInsurance = anyDisabilityInsuranceAmount > 0;
		data.request.isFatalAccident = fatalAccidentInsuranceAmount > 0;
		data.request.isAccidentDisability = accidentDisabilityInsuranceAmount > 0;
		data.request.isDreadDisease = dreadDiseaseInsuranceAmount > 0;
		data.request.insuranceDurationYears = durationYears;

		const multiplier = FeatureFlagConfig.isEnabled(FEATURE_FLAG_NAME) ? 1 : 0.85;

		if (data.request.isCreditInsurance) {
			data.response.insurancePriceCredit = Math.round(riskPrice * multiplier);
			data.response.insuranceAmountCredit = Math.round(riskInsuranceAmount);
		}

		if (data.request.isRiskInsurance) {
			data.response.insurancePriceRisk = Math.round(riskPrice * multiplier);
			data.response.insuranceAmountRisk = Math.round(riskInsuranceAmount);
		}
		data.response.insuranceAmountDisability = Math.round(anyDisabilityInsuranceAmount);
		data.response.insuranceAmountFatalAccident = Math.round(fatalAccidentInsuranceAmount);
		data.response.insuranceAmountAccidentDisability = Math.round(accidentDisabilityInsuranceAmount);
		data.response.insuranceAmountDreadDisease = Math.round(dreadDiseaseInsuranceAmount);
		data.response.insurancePriceDisability = Math.round(anyDisabilityInsurancePrice * multiplier);
		data.response.insurancePriceFatalAccident = Math.round(fatalAccidentInsurancePrice * multiplier);
		data.response.insurancePriceAccidentDisability = Math.round(accidentDisabilityInsurancePrice * multiplier);
		data.response.insurancePriceDreadDisease = Math.round(dreadDiseaseInsurancePrice * multiplier);

		return data;
	}

	protected getAutoresponderName(): string {
		return FeatureFlagConfig.isEnabled(FEATURE_FLAG_NAME)
			? "autoresponder-risk-insurance"
			: "autoresponder-risk-insurance-old";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { firstName, lastName, durationYears, riskInsuranceAmount, mainInsuranceGoal },
			calculatorResponse,
		} = this.state;
		const calculatorResponses = Object.values(calculatorResponse);

		return {
			lastName,
			firstName,

			durationYears,
			riskInsuranceAmount: formatText(riskInsuranceAmount, "ft"),
			creditInsurance: mainInsuranceGoal === "CREDIT_INSURANCE",
			riskInsurance: mainInsuranceGoal === "RISK_INSURANCE",

			...(FeatureFlagConfig.isEnabled(FEATURE_FLAG_NAME)
				? {
						result1: this.getAutoresponderCalculatorResult(calculatorResponses?.[0]),
						result2: this.getAutoresponderCalculatorResult(calculatorResponses?.[1]),
						result3: this.getAutoresponderCalculatorResult(calculatorResponses?.[2]),
					}
				: this.getAutoresponderCalculatorResult(calculatorResponses?.[0])),
		};
	}

	private getAutoresponderCalculatorResult(
		calculatorResponse?: RiskInsuranceResponse,
	): Record<string, string | undefined> {
		if (!calculatorResponse) {
			return {};
		}

		const {
			anyDisabilityInsuranceAmount,
			fatalAccidentInsuranceAmount,
			dreadDiseaseInsuranceAmount,
			accidentDisabilityInsuranceAmount,
			dreadDiseaseInsurancePrice,
			accidentDisabilityInsurancePrice,
			anyDisabilityInsurancePrice,
			fatalAccidentInsurancePrice,
			sumPrice,
			riskPrice,
		} = calculatorResponse;

		const multiplier = FeatureFlagConfig.isEnabled(FEATURE_FLAG_NAME) ? 1 : 0.85;

		return {
			anyDisabilityInsuranceAmount: anyDisabilityInsuranceAmount
				? formatText(anyDisabilityInsuranceAmount, "ft")
				: undefined,
			fatalAccidentInsuranceAmount: fatalAccidentInsuranceAmount
				? formatText(fatalAccidentInsuranceAmount, "ft")
				: undefined,
			dreadDiseaseInsuranceAmount: dreadDiseaseInsuranceAmount
				? formatText(dreadDiseaseInsuranceAmount, "ft")
				: undefined,
			accidentDisabilityInsuranceAmount: accidentDisabilityInsuranceAmount
				? formatText(accidentDisabilityInsuranceAmount, "ft")
				: undefined,

			dreadDiseaseInsurancePrice: formatText(dreadDiseaseInsurancePrice * multiplier, "ft"),
			accidentDisabilityInsurancePrice: formatText(accidentDisabilityInsurancePrice * multiplier, "ft"),
			anyDisabilityInsurancePrice: formatText(anyDisabilityInsurancePrice * multiplier, "ft"),
			fatalAccidentInsurancePrice: formatText(fatalAccidentInsurancePrice * multiplier, "ft"),

			sumPrice: formatText(sumPrice * multiplier, "ft"),
			riskPrice: formatText(riskPrice * multiplier, "ft"),
		};
	}

	protected getProductType(): ProductType {
		return "LIFE_INSURANCE";
	}

	protected getClassName(): string {
		return `${super.getClassName()} risk-insurance`;
	}

	private sendViewItemListEvent(): void {
		const {
			calculatorResponse,
			values: { durationYears },
		} = this.state;
		const cheapestOffer = getCheapestOffer(calculatorResponse);

		this.sendEvent("view_item_list", {
			item_list_id: this.calculationId,
			item_list_name: "normal",
			items: [
				{
					item_id: "",
					item_brand: "",
					item_name: "",
					affiliation: "product",
					index: 1,
					coupon: "none",
					currency: "HUF",
					discount: 0,
					item_category: "",
					item_category2: "",
					item_category3: "",
					item_category4: "",
					item_category5: "",
					item_variant: "",
					price: Math.floor(cheapestOffer?.sumPrice),
					quantity: durationYears,
				},
			],
		});
	}

	protected getComponentName(): string {
		return "RiskInsuranceSmallCalculator";
	}
}

export default RiskInsuranceSmallCalculator;
